import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { MessageSquareReply, CalendarClock, TrendingUp, Clock } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

const silkEase = [0.22, 1, 0.36, 1] as const;

const sectionVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: silkEase } },
};

const icons = [MessageSquareReply, CalendarClock, TrendingUp, Clock];

export function Solution() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  const { language } = useLanguage();
  const t = translations[language].solution;

  return (
    <section
      className="py-28 px-6"
      style={{
        background: "var(--surface-1)",
        borderTop: "1px solid var(--border)",
      }}
    >
      <motion.div
        ref={sectionRef}
        className="max-w-5xl mx-auto"
        variants={sectionVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <motion.div className="text-center mb-14" variants={itemVariants}>
          <h2
            className="text-4xl md:text-5xl mb-4"
            style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          >
            {t.heading}{" "}
            <em style={{ color: "var(--gold)", fontStyle: "italic" }}>{t.headingAccent}</em>
          </h2>
          <p
            className="text-lg max-w-2xl mx-auto"
            style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
          >
            {t.description}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5 mb-14">
          {t.items.map((item, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={i}
                className="flex gap-4 p-6 rounded-2xl"
                style={{
                  border: "1px solid var(--border)",
                  background: "var(--surface-2)",
                }}
                variants={itemVariants}
                whileHover={{
                  y: -4,
                  borderColor: "var(--border-strong)",
                  transition: { duration: 0.25, ease: "easeOut" },
                }}
              >
                <span
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(212, 149, 106, 0.08)",
                    border: "1px solid var(--border)",
                    color: "var(--gold)",
                  }}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <div>
                  <h3
                    className="text-lg mb-1.5"
                    style={{ fontFamily: "var(--font-display)", color: "var(--cream)" }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
                  >
                    {item.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          className="text-xl md:text-2xl text-center leading-relaxed"
          style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          variants={itemVariants}
        >
          {t.closing}{" "}
          <em style={{ color: "var(--gold)", fontStyle: "italic" }}>{t.closingAccent}</em>
        </motion.p>
      </motion.div>
    </section>
  );
}
